import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Baby,
  Heart,
  Activity,
  Calendar,
  AlertCircle,
  User,
  Search,
  Loader2,
  FileText
} from 'lucide-react';
import { reportService } from '@/services/reportService';
import { patientService } from '@/services/patientService';
import { Patient } from '@/types/patient.types';
import { useToast } from '@/hooks/use-toast';
import { formatDate } from '@/utils/formatters';

interface TimelineEvent {
  date: string;
  type: string;
  title: string;
  description: string;
  icon: string;
}

interface PatientInfo {
  id: number;
  name: string;
  birth_date: string;
  hospital: string;
}

interface ApiResponse {
  patient_info: PatientInfo;
  timeline: TimelineEvent[];
  statistics: {
    total_events: number;
    diagnoses_count: number;
    procedures_count: number;
    markers_count: number;
  };
}

type SearchType = 'cnp' | 'name';

export const PatientTimelineReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ApiResponse | null>(null);
  const [searchType, setSearchType] = useState<SearchType>('cnp');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setError('Introduceți termenul de căutare');
      return;
    }

    setSearching(true);
    setError(null);
    setSearchResults([]);
    setData(null);

    try {
      if (searchType === 'cnp') {
        const results = await patientService.searchPatients(searchTerm.trim());

        if (results.length === 0) {
          setError('Nu a fost găsit niciun pacient cu acest CNP');
          return;
        }

        if (results.length === 1) {
          await fetchTimeline(results[0].id.toString());
        } else {
          setSearchResults(results);
        }
      } else {
        const results = await patientService.searchPatients(searchTerm.trim());

        if (results.length === 0) {
          setError('Nu a fost găsit niciun pacient cu acest nume');
          return;
        }

        setSearchResults(results);
      }
    } catch (err: any) {
      setError(err.message || 'Eroare la căutarea pacientului');
    } finally {
      setSearching(false);
    }
  };

  const handlePatientSelect = (patient: Patient) => {
    fetchTimeline(patient.id.toString());
    setSearchResults([]);
  };

  const handleReset = () => {
    setSearchTerm('');
    setSearchResults([]);
    setData(null);
    setError(null);
  };

  const getPlaceholder = () => {
    if (searchType === 'cnp') {
      return 'Introduceți CNP-ul pacientului (13 cifre)';
    }
    return 'Introduceți numele pacientului';
  };

  const fetchTimeline = async (patientId: string) => {
    if (!patientId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await reportService.getPatientTimeline(parseInt(patientId));

      // Transform the API response to match our expected format
      const transformedData: ApiResponse = {
        patient_info: {
          id: response.patient_id,
          name: response.patient_name,
          birth_date: response.milestones.birth_date,
          hospital: '',
        },
        timeline: response.events.map(event => ({
          date: event.date,
          type: event.type,
          title: event.title,
          description: event.description,
          icon: getIconForEventType(event.type),
        })),
        statistics: {
          total_events: response.events.length,
          diagnoses_count: response.events.filter(e => e.type === 'diagnosis').length,
          procedures_count: response.events.filter(e => e.type === 'procedure').length,
          markers_count: response.events.filter(e => e.type === 'outcome').length,
        },
      };

      setData(transformedData);
    } catch (error) {
      console.error('Error fetching timeline:', error);
      setError('Nu s-au putut încărca datele cronologiei pacientului');
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele cronologiei pacientului',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getIconForEventType = (type: string) => {
    switch (type) {
      case 'birth':
        return 'baby';
      case 'diagnosis':
        return 'heart';
      case 'procedure':
        return 'activity';
      default:
        return 'calendar';
    }
  };

  const getEventIcon = (iconName: string) => {
    switch (iconName) {
      case 'baby':
        return <Baby className="h-4 w-4" />;
      case 'heart':
        return <Heart className="h-4 w-4" />;
      case 'activity':
        return <Activity className="h-4 w-4" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'birth':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'diagnosis':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'procedure':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Search Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Căutare pacient</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-4 items-end">
              <div className="w-48">
                <Label htmlFor="searchType">Tip căutare</Label>
                <Select value={searchType} onValueChange={(value: SearchType) => {
                  setSearchType(value);
                  setSearchTerm('');
                  setSearchResults([]);
                  setError(null);
                }}>
                  <SelectTrigger id="searchType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cnp">CNP</SelectItem>
                    <SelectItem value="name">Nume</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label htmlFor="searchTerm">
                  {searchType === 'cnp' ? 'CNP' : 'Nume'}
                </Label>
                <Input
                  id="searchTerm"
                  type="text"
                  placeholder={getPlaceholder()}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <Button onClick={handleSearch} disabled={searching || loading}>
                {searching ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Căutare
              </Button>
              {(data || searchResults.length > 0) && (
                <Button variant="outline" onClick={handleReset}>
                  Resetare
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Rezultate căutare ({searchResults.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {searchResults.map((patient) => (
                <Card
                  key={patient.id}
                  className="cursor-pointer hover:bg-accent transition-colors"
                  onClick={() => handlePatientSelect(patient)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div className="flex-1 space-y-1">
                        <p className="font-medium">{patient.name}</p>
                        <p className="text-sm text-muted-foreground">
                          Cod: {patient.patient_code}
                        </p>
                        {patient.cnp && (
                          <p className="text-sm text-muted-foreground">
                            CNP: {patient.cnp}
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground">
                          Data nașterii: {new Date(patient.birth_date).toLocaleDateString('ro-RO')}
                        </p>
                        {patient.hospital_name && (
                          <p className="text-sm text-muted-foreground">
                            Spital: {patient.hospital_name}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {loading && (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      )}

      {!loading && data && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Informații pacient
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Nume</p>
                  <p className="font-medium">{data.patient_info.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Data nașterii</p>
                  <p className="font-medium">{formatDate(data.patient_info.birth_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Spital</p>
                  <p className="font-medium">{data.patient_info.hospital || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Statistici evenimente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{data.statistics.total_events}</p>
                  <p className="text-sm text-muted-foreground">Total evenimente</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{data.statistics.diagnoses_count}</p>
                  <p className="text-sm text-muted-foreground">Diagnostice</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{data.statistics.procedures_count}</p>
                  <p className="text-sm text-muted-foreground">Proceduri</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{data.statistics.markers_count}</p>
                  <p className="text-sm text-muted-foreground">Markeri</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Cronologie evenimente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative space-y-6">
                {data.timeline.length === 0 ? (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Nu există evenimente înregistrate pentru acest pacient
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-4">
                    {data.timeline.map((event, index) => (
                      <div key={index} className="flex gap-4 relative">
                        {index !== data.timeline.length - 1 && (
                          <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gray-200" />
                        )}
                        <div className={`flex-shrink-0 h-12 w-12 rounded-full border-2 flex items-center justify-center ${getEventColor(event.type)}`}>
                          {getEventIcon(event.icon)}
                        </div>
                        <div className="flex-1 bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-semibold">{event.title}</p>
                                <Badge variant="outline" className="text-xs">
                                  {event.type === 'birth' ? 'Naștere' :
                                    event.type === 'diagnosis' ? 'Diagnostic' :
                                      event.type === 'procedure' ? 'Procedură' :
                                        event.type}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mb-2">{event.description}</p>
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(event.date)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {!data && !loading && !searching && searchResults.length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            Căutați un pacient după CNP sau nume pentru a vizualiza cronologia
          </p>
        </div>
      )}
    </div>
  );
};
